import { gql } from '@apollo/client';
import phone from 'phone';
import validator from 'validator';
import { TestContext } from 'yup';
import { isPhoneNumber } from 'class-validator';

import { gqlTypes } from '../types/gqlTypes';

export const isValidPhoneNumber = (value: string | null | undefined, testContext: TestContext) => {
  // If the field is nullable and the value is null or undefined, we consider it valid
  if (testContext.schema.nullable && (value === null || value === undefined)) {
    return true;
  }

  if (!value) {
    return false;
  }
  return phone(value, { validateMobilePrefix: false }).isValid;
};

export function isValidUSPhoneNumber(value: string | null | undefined, testContext: TestContext) {
  // If the field is nullable and the value is null or undefined, we consider it valid
  if (testContext.schema.nullable && (value === null || value === undefined)) {
    return true;
  }

  if (!value) {
    return false;
  }
  return isPhoneNumber(value, 'US') || isPhoneNumber(value, 'PR') || isPhoneNumber(value, 'VI');
}

const KG_TO_LBS = 2.20462;

export const kgToLbs = (input: number) => input * KG_TO_LBS;

export const lbsToKg = (input: number) => input / KG_TO_LBS;

export const saneEmail = (email: string | null | undefined) => !email || !email.endsWith('.con');

export const isOptionalUrl = (url: string | null | undefined) => (url ? validator.isURL(url) : true);

export const userChipResellerQuery = gql`
  query userChipReseller {
    currentUser {
      chipReseller {
        id
        status
        verified
      }
    }
  }
`;

export const RESCUE_PORTAL_BETA_TESTER_FLAG = 'rescuePortal:betaTester:enabled';

// Hand-typed rather than added to `gqlTypes`: codegen introspects a live web API, and the
// redirect decision is the only consumer. `status` is selected here so the caller can make both
// the portal and the legacy reseller decision from one round-trip.
export const rescuePortalAccessQuery = gql`
  query rescuePortalAccess {
    currentUser {
      settings {
        rescueOrganizationAccess
      }
      chipReseller {
        id
        status
      }
      targetedFeatureFlags(names: ["rescuePortal:betaTester:enabled"]) {
        name
        value
      }
    }
  }
`;

export interface RescuePortalAccessData {
  currentUser: {
    settings: { rescueOrganizationAccess: boolean };
    chipReseller: { id: string; status: gqlTypes.ResellerStatus } | null;
    targetedFeatureFlags: { name: string; value: boolean }[] | null;
  } | null;
}

/**
 * `settings.rescueOrganizationAccess` is the same predicate every server-side rescue gate uses, so
 * client and server can't drift — a reseller typed RESCUE without the backing access would other-
 * wise land on portal queries that throw Forbidden. A still-pending application stays on the legacy
 * flow, matching the non-portal branch in `Login`. The beta flag is honoured unconditionally so the
 * original cohort keeps working and can be retired on its own.
 */
export function belongsInRescuePortal(data?: RescuePortalAccessData | null) {
  const currentUser = data?.currentUser;
  const hasBetaFlag = !!currentUser?.targetedFeatureFlags?.some(
    (flag) => flag.name === RESCUE_PORTAL_BETA_TESTER_FLAG && flag.value,
  );
  if (hasBetaFlag) {
    return true;
  }

  const applicationPending = currentUser?.chipReseller?.status === gqlTypes.ResellerStatus.PENDING;
  return !!currentUser?.settings?.rescueOrganizationAccess && !applicationPending;
}
